import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  INTERVAL_OPTIONS_SECONDS,
  WINDOW_OPTIONS_MINUTES,
  loadAutoDeactivateSettings,
  saveAutoDeactivateSettings
} from '../utils/autoDeactivateSettings';
import {
  Actions,
  BrandHeaderRow,
  BrandLogo,
  Button,
  Card,
  Header,
  HeaderIconButton,
  HeaderText,
  HomeIcon,
  Input,
  Page,
  Select,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import {
  DISTANCE_UNIT_OPTIONS,
  DISTANCE_UNITS,
  getDistanceUnit
} from '../utils/distanceUnits';
import {
  loadDefaultLocationRadius,
  saveDefaultLocationRadius
} from '../utils/defaultLocationRadius';
import { isPersonalApp } from '../utils/appMode';

const PLEDGE_REMINDER_OPTIONS = [
  { value: 5, label: '5 min' },
  { value: 10, label: '10 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hr' },
  { value: 120, label: '2 hr' },
  { value: 240, label: '4 hr' }
];

const SettingsPanel = styled(Card)`
  display: grid;
  gap: 0.85rem;
`;

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const SettingRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.1);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.045);

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const SettingGroup = styled.div`
  display: grid;
  gap: 0;
  padding: 0.9rem;
  border: 1px solid rgba(251, 191, 36, 0.22);
  border-radius: 26px;
  background:
    linear-gradient(135deg, rgba(251, 191, 36, 0.08), rgba(96, 165, 250, 0.05)),
    rgba(255, 255, 255, 0.035);
`;

const GroupedSettingRow = styled(SettingRow)`
  border-color: transparent;
  border-radius: 18px;
  background: transparent;

  & + & {
    border-top-color: rgba(226, 232, 240, 0.11);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  &:not(:last-child) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const ToggleSettingRow = styled(GroupedSettingRow)`
  @media (max-width: 640px) {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: start;
  }
`;

const SettingText = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const SettingTitle = styled.span`
  color: #f8fafc;
  font-size: 1.1rem;
  font-weight: 950;
`;

const SettingDescription = styled.span`
  max-width: 34rem;
  color: rgba(203, 213, 225, 0.68);
  font-weight: 700;
  line-height: 1.35;
`;

const CompactSelect = styled(Select)`
  width: auto;
  min-width: 140px;
`;

const RadiusControls = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, 130px) minmax(120px, 160px);
  gap: 0.65rem;

  @media (max-width: 640px) {
    width: 100%;
    grid-template-columns: 1fr;
  }
`;

const ProfileControls = styled.form`
  display: grid;
  grid-template-columns: minmax(180px, 280px) auto;
  gap: 0.65rem;
  align-items: center;

  @media (max-width: 640px) {
    width: 100%;
    grid-template-columns: 1fr;
  }
`;

const CompactInput = styled(Input)`
  min-width: 0;
`;

const ReminderControls = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 150px);
  gap: 0.65rem;
  align-items: center;

  @media (max-width: 640px) {
    width: 100%;
    grid-template-columns: 1fr;
  }
`;

const Checkbox = styled.input`
  width: 26px;
  height: 26px;
  accent-color: #fbbf24;
`;

const DangerRow = styled(SettingRow)`
  border-color: rgba(248, 113, 113, 0.32);
  background: rgba(127, 29, 29, 0.18);
`;

const DangerButton = styled(Button)`
  background: linear-gradient(135deg, #ef4444, #991b1b);
  color: #fff7ed;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const ErrorMessage = styled.div`
  padding: 0.85rem 1rem;
  border: 1px solid rgba(248, 113, 113, 0.38);
  border-radius: 18px;
  background: rgba(127, 29, 29, 0.28);
  color: #fecaca;
  font-weight: 850;
`;

const SuccessMessage = styled(ErrorMessage)`
  border-color: rgba(34, 197, 94, 0.38);
  background: rgba(20, 83, 45, 0.24);
  color: #bbf7d0;
`;

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout, updateUserProfile } = useAuth();
  const canUsePersonalFeatures = isPersonalApp();
  const [settings, setSettings] = useState(loadAutoDeactivateSettings);
  const [defaultRadius, setDefaultRadius] = useState(loadDefaultLocationRadius);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [reminderEnabled, setReminderEnabled] = useState(user?.pledgeReminderEnabled === true);
  const [reminderMinutes, setReminderMinutes] = useState(user?.pledgeReminderMinutesBefore || 15);
  const [reminderError, setReminderError] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const [isSavingReminder, setIsSavingReminder] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  useEffect(() => {
    setDisplayName(user?.displayName || '');
  }, [user?.displayName]);

  useEffect(() => {
    setReminderEnabled(user?.pledgeReminderEnabled === true);
    setReminderMinutes(user?.pledgeReminderMinutesBefore || 15);
  }, [user?.pledgeReminderEnabled, user?.pledgeReminderMinutesBefore]);

  const updateSettings = (updates) => {
    setSettings(saveAutoDeactivateSettings({
      ...settings,
      ...updates
    }));
  };

  const updateDefaultRadius = (updates) => {
    setDefaultRadius(saveDefaultLocationRadius({
      ...defaultRadius,
      ...updates
    }));
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();

    setIsSavingProfile(true);
    setProfileError('');
    setProfileMessage('');

    try {
      const nextDisplayName = displayName.trim();

      await updateUserProfile({
        displayName: nextDisplayName,
        completeNameOnboarding: true
      });
      setDisplayName(nextDisplayName);
      setProfileMessage('Name saved.');
    } catch (error) {
      setProfileError(error.response?.data?.message || error.message || 'Failed to save name');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const saveReminderSettings = async ({
    enabled,
    minutes,
    previousEnabled,
    previousMinutes
  }) => {
    setIsSavingReminder(true);
    setReminderError('');
    setReminderMessage('');

    try {
      await updateUserProfile({
        pledgeReminderEnabled: enabled,
        pledgeReminderMinutesBefore: Number(minutes)
      });
      setReminderMessage(enabled ? 'Reminder saved.' : 'Reminder turned off.');
    } catch (error) {
      setReminderEnabled(previousEnabled);
      setReminderMinutes(previousMinutes);
      setReminderError(error.response?.data?.message || error.message || 'Failed to save reminder');
    } finally {
      setIsSavingReminder(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm('Delete your PlacePledge account and all saved data? This cannot be undone.');

    if (!confirmed) {
      return;
    }

    setIsDeletingAccount(true);
    setDeleteError('');

    try {
      const idToken = await user.getIdToken();
      await axios.delete(`${window.RUNTIME_CONFIG.apiUrl}/user`, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      try {
        await logout();
      } catch (error) {
        console.warn('Sign out after account deletion failed:', error);
      }

      navigate('/login', { replace: true });
    } catch (error) {
      setDeleteError(error.response?.data?.message || error.message || 'Failed to delete account');
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <Page>
      <Shell>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Home" onClick={() => navigate('/dashboard')}>
                  <HomeIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>Settings.</Title>
            <Subtitle>{user?.email || 'Guest account'}</Subtitle>
          </HeaderText>
        </Header>

        <SettingsPanel>
          <SettingRow as="div">
            <SettingText>
              <SettingTitle>Name in emails</SettingTitle>
              <SettingDescription>Used when PlacePledge sends accountability emails.</SettingDescription>
            </SettingText>
            <ProfileControls onSubmit={handleSaveProfile}>
              <CompactInput
                type="text"
                autoComplete="name"
                value={displayName}
                placeholder="Your name"
                aria-label="Name in emails"
                maxLength={80}
                onChange={(event) => {
                  setDisplayName(event.target.value);
                  setProfileError('');
                  setProfileMessage('');
                }}
                disabled={isSavingProfile}
                required
              />
              <Button type="submit" disabled={isSavingProfile || !displayName.trim()}>
                {isSavingProfile ? 'Saving...' : 'Save'}
              </Button>
            </ProfileControls>
          </SettingRow>
          {profileMessage && <SuccessMessage>{profileMessage}</SuccessMessage>}
          {profileError && <ErrorMessage>{profileError}</ErrorMessage>}

          <SettingRow as="div">
            <SettingText>
              <SettingTitle>Pledge reminders</SettingTitle>
              <SettingDescription>Email me before an active pledge reaches its deadline.</SettingDescription>
            </SettingText>
            <ReminderControls>
              <CompactSelect
                aria-label="Reminder time"
                value={reminderEnabled ? reminderMinutes : 'off'}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  const nextEnabled = nextValue !== 'off';
                  const nextMinutes = nextEnabled ? Number(nextValue) : reminderMinutes;
                  const previousEnabled = reminderEnabled;
                  const previousMinutes = reminderMinutes;

                  setReminderEnabled(nextEnabled);
                  setReminderMinutes(nextMinutes);
                  saveReminderSettings({
                    enabled: nextEnabled,
                    minutes: nextMinutes,
                    previousEnabled,
                    previousMinutes
                  });
                }}
                disabled={isSavingReminder}
              >
                <option value="off">Off</option>
                {PLEDGE_REMINDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CompactSelect>
            </ReminderControls>
          </SettingRow>
          {reminderMessage && <SuccessMessage>{reminderMessage}</SuccessMessage>}
          {reminderError && <ErrorMessage>{reminderError}</ErrorMessage>}

          <SettingRow as="div">
            <SettingText>
              <SettingTitle>Manage pledges</SettingTitle>
              <SettingDescription>Edit or delete pledges during the 3-minute window after creation.</SettingDescription>
            </SettingText>
            <Button type="button" onClick={() => navigate('/manage-pledges')}>
              Manage
            </Button>
          </SettingRow>

          <SettingRow as="div">
            <SettingText>
              <SettingTitle>Accountability contacts</SettingTitle>
              <SettingDescription>Add contacts and copy invite links for pledge notifications.</SettingDescription>
            </SettingText>
            <Button type="button" onClick={() => navigate('/manage-contacts')}>
              Manage
            </Button>
          </SettingRow>

          {canUsePersonalFeatures && (
            <SettingRow as="div">
              <SettingText>
                <SettingTitle>Locations</SettingTitle>
                <SettingDescription>Add, edit, or delete the places you can pledge to reach.</SettingDescription>
              </SettingText>
              <Button
                type="button"
                onClick={() => navigate('/manage-locations', { state: { backTo: '/settings' } })}
              >
                Manage
              </Button>
            </SettingRow>
          )}

          <SettingRow as="div">
            <SettingText>
              <SettingTitle>Default radius</SettingTitle>
              <SettingDescription>Used for locations created from the pledge map.</SettingDescription>
            </SettingText>
            <RadiusControls>
              <CompactInput
                type="number"
                aria-label="Default radius value"
                min={getDistanceUnit(defaultRadius.unit).min}
                step={getDistanceUnit(defaultRadius.unit).step}
                value={defaultRadius.value}
                onChange={(event) => updateDefaultRadius({ value: event.target.value })}
              />
              <CompactSelect
                aria-label="Default radius unit"
                value={defaultRadius.unit}
                onChange={(event) => updateDefaultRadius({
                  unit: event.target.value,
                  value: DISTANCE_UNITS[event.target.value].defaultValue
                })}
              >
                {DISTANCE_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CompactSelect>
            </RadiusControls>
          </SettingRow>

          {canUsePersonalFeatures && (
            <SettingGroup>
              <ToggleSettingRow>
                <SettingText>
                  <SettingTitle>Auto fulfillment</SettingTitle>
                  <SettingDescription>Try to fulfill active pledges while the dashboard tab is visible.</SettingDescription>
                </SettingText>
                <Checkbox
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(event) => updateSettings({ enabled: event.target.checked })}
                />
              </ToggleSettingRow>

              {settings.enabled && (
                <>
                  <GroupedSettingRow>
                    <SettingText>
                      <SettingTitle>Try every</SettingTitle>
                      <SettingDescription>How often the dashboard checks whether a pledge can be fulfilled.</SettingDescription>
                    </SettingText>
                    <CompactSelect
                      value={settings.intervalSeconds}
                      onChange={(event) => updateSettings({ intervalSeconds: Number(event.target.value) })}
                    >
                      {INTERVAL_OPTIONS_SECONDS.map((seconds) => (
                        <option key={seconds} value={seconds}>
                          {seconds} sec
                        </option>
                      ))}
                    </CompactSelect>
                  </GroupedSettingRow>

                  <GroupedSettingRow>
                    <SettingText>
                      <SettingTitle>Start within</SettingTitle>
                      <SettingDescription>Only try fulfillment near the pledge time.</SettingDescription>
                    </SettingText>
                    <CompactSelect
                      value={settings.windowMinutes}
                      onChange={(event) => updateSettings({ windowMinutes: Number(event.target.value) })}
                    >
                      {WINDOW_OPTIONS_MINUTES.map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {minutes} min
                        </option>
                      ))}
                    </CompactSelect>
                  </GroupedSettingRow>
                </>
              )}
            </SettingGroup>
          )}

          <DangerRow as="div">
            <SettingText>
              <SettingTitle>Delete account and data</SettingTitle>
              <SettingDescription>Remove your account, pledges, contacts, locations, and saved settings.</SettingDescription>
            </SettingText>
            <DangerButton type="button" onClick={handleDeleteAccount} disabled={isDeletingAccount}>
              {isDeletingAccount ? 'Deleting...' : 'Delete my account & data'}
            </DangerButton>
          </DangerRow>

          {deleteError && <ErrorMessage>{deleteError}</ErrorMessage>}
        </SettingsPanel>
      </Shell>
    </Page>
  );
};

export default Settings;
